<template>
  <Card v-if="isPayCompleted" style="width: 100%">
    <VRow>
      <VCol>
        <ThankYou class="w-100">
          <template v-slot:title>
            <span class="text-main-color">Thanks!</span>
          </template>
          <template v-slot:text> Payment method updated successfully. </template>
        </ThankYou>
      </VCol>
    </VRow>
  </Card>
  <template v-else>
    <Card class="w-100">
      <template v-if="isStripeLoaded">
        <VForm v-model="formValid" @submit.prevent>
          <VRow>
            <VCol cols="12">
              <div class="text-title-color font-size-16 font-weight-600 mb-3">Personal Details</div>
              <div id="address-element"></div>
            </VCol>
            <VCol cols="12">
              <label>Card Number</label>
              <div id="card-element"></div>
            </VCol>
            <VCol cols="12">
              <div class="d-flex flex-column align-center justify-center">
                <VCheckbox v-model="isAgreeTermsConditions" :disabled="isPayLoading" :rules="formRules.terms">
                  <template v-slot:label>
                    <div>
                      I agree to XtraPension's
                      <a href="https://www.xtrapension.com/terms" target="_blank" v-bind="props" @click.stop>
                        Terms & Conditions</a
                      >
                    </div>
                  </template>
                </VCheckbox>
              </div>
            </VCol>
            <VCol cols="12">
              <div v-if="isPayLoading" class="d-flex flex-column align-center justify-center py-4 w-100">
                <VProgressCircular indeterminate color="primary" />
              </div>
              <div class="d-flex items-center justify-center w-100">
                <button
                  class="main-btn"
                  :disabled="isPayLoading || !isAgreeTermsConditions"
                  @click="pay()"
                  type="submit"
                >
                  Update Payment Method
                </button>
              </div>
              <div v-if="stripeValidationError" class="info-container info-container__red my-2">
                <p>{{ stripeValidationError }}</p>
              </div>
            </VCol>
          </VRow>
        </VForm>
      </template>
      <template v-else>
        <div class="d-flex flex-column align-center justify-center py-4 w-100">
          <VProgressCircular indeterminate color="primary" />
        </div>
      </template>
    </Card>
  </template>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';
// Service
import StripeService from '@/services/stripe-service/stripe.service';
import applicationService from '@/services/application-service/application.service';
// Componens
import Card from '../Card/Card.vue';
import ThankYou from '../ThankYou/ThankYou.vue';
export default {
  components: {
    Card,
    ThankYou
  },
  props: {
    customerId: {
      type: String,
      required: true
    }
  },
  computed: {
    ...mapGetters('user', ['application'])
  },
  data() {
    return {
      isStripeLoaded: false,
      isPayLoading: false,
      isPayCompleted: false,
      isCountrySelected: false,
      countrySelected: 'Canada',
      payCompletedId: null,
      isAppCreateLoading: false,
      isAppCreateError: false,
      stripe: null,
      cardElement: null,
      addressElement: null,
      fullName: null,
      firstName: null,
      lastName: null,
      shippingAddress: null,
      phone: null,
      stripeValidationError: '',
      tax: true,
      customer: null,
      isAgreeTermsConditions: false,
      latest_charge: null,
      formValid: false,
      formRules: {
        terms: [(v) => !!v || 'Terms required']
      }
    };
  },
  async created() {
    if (this.customerId) {
      this.showStripeLoading();
      try {
        // eslint-disable-next-line no-undef
        this.stripe = await Stripe(process.env.VUE_APP_STRIPE_PUBLIC_KEY);
        await this.hideStripeLoading();
        this.createAndMountFormElements();
      } catch (err) {
        console.error(err);
      }
    }
  },
  methods: {
    ...mapActions('main', ['showSnackbar']),
    showStripeLoading() {
      this.isStripeLoaded = false;
    },
    hideStripeLoading() {
      this.isStripeLoaded = true;
    },
    async createAndMountFormElements() {
      const elements = this.stripe.elements();
      this.cardElement = elements.create('card', {
        hidePostalCode: true
      });
      this.cardElement.mount('#card-element');
      let options = {
        mode: 'billing',
        fields: {
          phone: 'always',
          name: 'auto'
        },
        display: {
          name: 'split'
        },
        validation: {
          phone: {
            required: 'always'
          }
        }
      };
      let defaultValues = {
        firstName: '',
        lastName: '',
        phone: '',
        address: {
          line1: '',
          city: '',
          state: '',
          country: '',
          postal_code: ''
        }
      };
      if (this.application?.firstName) {
        defaultValues.firstName = this.application?.firstName;
      }
      if (this.application?.lastName) {
        defaultValues.lastName = this.application?.lastName;
      }
      if (this.application?.phone) {
        defaultValues.phone = this.application?.phone;
      }
      if (this.application?.currentAddress) {
        defaultValues.address.line1 = this.application?.currentAddress?.street ?? '';
        defaultValues.address.city = this.application?.currentAddress?.city ?? '';
        defaultValues.address.state = this.application?.currentAddress?.state ?? '';
        defaultValues.address.country = this.application.currentAddress?.countryCode ?? '';
        defaultValues.address.postal_code = this.application.currentAddress?.postalCode ?? '';
      }

      options = {
        ...options,
        defaultValues: defaultValues
      };
      this.addressElement = elements.create('address', options);
      this.addressElement.mount('#address-element');

      this.cardElement.on('change', this.setValidationCardError);
      this.addressElement.on('change', this.setValidationError);
    },
    setValidationCardError(event) {
      this.stripeValidationError = event.error ? event.error.message : '';
    },
    setValidationError(event) {
      const { error, address, name, firstName, lastName, phone } = event.value;
      this.stripeValidationError = error ? error?.message : '';
      if (event.complete) {
        this.shippingAddress = address;
        this.fullName = name;
        this.firstName = firstName;
        this.lastName = lastName;
        this.phone = phone;
      } else {
        this.shippingAddress = null;
        this.fullName = null;
        this.firstName = null;
        this.lastName = null;
        this.phone = null;
      }
    },
    async pay() {
      try {
        if (this.formValid === true) {
          if (this.fullName && this.phone && this.shippingAddress) {
            this.showPayLoading();

            const createResp = await StripeService.createSetupIntent({
              customerId: this.customerId
            });

            if (createResp.error) {
              throw new Error(createResp.error);
            }

            const clientSecret = createResp.clientSecret;
            if (!clientSecret) {
              throw new Error('No client secret returned from server');
            }
            const result = await this.stripe.confirmCardSetup(clientSecret, {
              payment_method: {
                card: this.cardElement,
                billing_details: {
                  name: this.fullName,
                  email: this.email,
                  phone: this.phone,
                  address: this.shippingAddress
                }
              }
            });
            await this.handleSetupResult(result);
          } else {
            this.stripeValidationError = 'Fill in your personal data!';
          }
        }
      } catch (err) {
        this.hidePayLoading();
        console.error(err);
        this.showSnackbar({ text: err?.message || 'Oops, Something Went Wrong', color: 'red' });
      }
    },
    async handleSetupResult(result) {
      try {
        if (result.error) {
          this.stripeValidationError = result.error.message;
          this.showSnackbar({ text: result.error.message || 'Oops, Something Went Wrong', color: 'red' });
          this.hidePayLoading();
          return;
        }
        const setupIntent = result.setupIntent;
        const paymentMethodId = setupIntent.payment_method;
        const setupIntentId = setupIntent.id;

        await StripeService.attachPaymentMethod({
          customerId: this.customerId,
          paymentMethodId: paymentMethodId
        });

        await applicationService.updatePaymentMethodBillingPage({
          paymentMethod: paymentMethodId,
          customerId: this.customerId
        });

        this.isPayCompleted = true;
        this.payCompletedId = setupIntentId;
        this.showSnackbar({ text: 'Card saved successfully', color: 'green' });
        this.hidePayLoading();
      } catch (err) {
        console.error(err);
        this.hidePayLoading();
        this.showSnackbar({ text: err?.message || 'Oops, Something Went Wrong', color: 'red' });
      }
    },
    showPayLoading() {
      this.isPayLoading = true;
    },
    hidePayLoading() {
      this.isPayLoading = false;
    },
    showCreateAppLoading() {
      this.isAppCreateLoading = true;
    },
    hideCreateAppLoading() {
      this.isAppCreateLoading = false;
    }
  }
};
</script>
